/**

- RoleReady -- Criteria Anchor Library
- src/data/criteriaAnchorLibrary.js
- 
- 18-role scorecard anchor data. Each role contains:
- - anchors[]: one object per criterion, matched by index to criteriaLibrary.js criteria[]
- - Each anchor object: criterionLabel, no[], maybe[], yes[], strongYes[] (4 bullets each)
- - complianceGatesMeta[]: level field ("hard" | "conditional") matched by index to
- ```
  criteriaLibrary.js complianceGates[]
  ```
- 
- SOURCE: RoleReady_ScorecardAnchors_Roles01to18_v1_1.docx (Kate-reviewed IP)
- DO NOT edit anchor bullet content without Kate review.
- Last updated: May 2026 · libraryStatus: approved-v1.1-pending-kate-Q2-2026-review
- 
- USAGE in generatePDF.js:
- import { criteriaAnchorLibrary } from './criteriaAnchorLibrary';
- const roleAnchors = criteriaAnchorLibrary[roleKey]; // e.g. 'builder', 'barista'
  */

export const criteriaAnchorLibrary = {

// ─── Role 01: Builder ───────────────────────────────────────────────────────
builder: {
anchors: [
{
criterionLabel: 'Trade skills and quality of work',
no: [
"Can't name a specific project they ran",
'Talks only in generalities',
'Just does what the foreman says',
'No self-checks before sign-off',
],
maybe: [
'Vague project examples',
'Some quality habits, but thin on detail',
'Relies on inspector to catch issues',
"Can't name what they personally verify",
],
yes: [
'Real projects at real addresses',
'Names specific quality checks they own',
'Catches issues before inspection',
'Takes pride in getting it right first time',
],
strongYes: [
'Raised the standard above what was required',
'Caught a structural error others missed',
'Has trained others in quality habits',
'Quality is something they enforce',
],
},
{
criterionLabel: 'Health and safety awareness',
no: [
"Can't name a specific hazard they spotted",
"Treats safety as the foreman's job",
'Cleans up only at end of job',
"Says 'I just keep my head down'",
],
maybe: [
'Reported a hazard but took no action',
'Tidiness is occasional, not routine',
'Sees safety briefings as something done to them',
'Hedges on personal responsibility',
],
yes: [
'Names a real hazard and what they did',
'Daily site tidy is automatic',
'Knows why it matters (safety, efficiency, client)',
"Stops work when something's not safe",
],
strongYes: [
'Has refused unsafe work or stopped a job',
"Changed a crew's habits after a near-miss",
'Talks about safety as something they own',
'Names the cost of cutting corners',
],
},
{
criterionLabel: 'Reading plans and specifications',
no: [
"Says they read plans but can't explain how",
'No cross-checking between plan and site',
'Relies on others to interpret drawings',
'No story of catching an error',
],
maybe: [
'Lists plan types but examples are thin',
"Says they'd query something, no real example",
'Some cross-referencing, surface-level',
'Hesitant when pressed on specifics',
],
yes: [
'Walks through plans, elevations, sections, details',
'Cross-checks plans against actual site',
"Queries things that don't add up",
'Real example of catching a paper problem early',
],
strongYes: [
'Caught an error worth days or thousands',
'Comfortable challenging architects/engineers',
'Has built or led teams that read plans the same way',
'Treats plan-reading as a craft',
],
},
{
criterionLabel: 'Reliability and time management',
no: [
'Just works harder when busy -- no system',
'Blames clients/trades when things slip',
'Bouncing between sites, no pattern',
'Drops balls under pressure',
],
maybe: [
"Informal system -- 'I remember things, mostly'",
'Reactive with client comms',
'Some unexplained gaps in work history',
'Finishes most jobs, hedges on a couple',
],
yes: [
'Has a system (notebook, phone, plan)',
'Communicates with clients proactively',
'Stable work history with logical moves',
'Stays calm and adjusts when things go sideways',
],
strongYes: [
'Run multiple jobs in parallel, finished all',
'Long stretches with same employers',
'Promoted off the tools because of reliability',
'The person crews rely on when things tighten',
],
},
{
criterionLabel: 'Communication with site team',
no: [
'Blames subbies or other trades for delays',
'Just does their bit and leaves',
'No questions about the team or how jobs run',
'Pattern of being pushed off sites',
],
maybe: [
"Conflict-free stories that don't ring true",
'Tension always with the other person at fault',
'Generic questions for the interviewer',
'Coordinates when forced, not by default',
],
yes: [
"Names sparkies, plumbers, painters they've worked with",
"Sequences work so trades don't clash",
'Real example of resolving a disagreement',
'Specific questions about the crew and boss',
],
strongYes: [
'Resolved a conflict that was holding up a job',
'Named by other trades as easy to work with',
'Asks questions about fitting in, not just being hired',
'Treats site harmony as part of their job',
],
},
],
complianceGatesMeta: [
{ label: 'Site Safe certification', level: 'conditional' },
{ label: 'Trade qualification or equivalent', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
{ label: 'Full Class 1 driver licence', level: 'conditional' },
],
},

// ─── Role 02: Barista ───────────────────────────────────────────────────────
barista: {
anchors: [
{
criterionLabel: 'Coffee quality and consistency',
no: [
"Can't describe their workflow under pressure",
'No sense of extraction, milk texture, or ratios',
'Talks about coffee vaguely',
'Relies on others to diagnose problems',
],
maybe: [
'Knows the basics, thin on detail',
'Describes speed but not quality',
'Some understanding of extraction, no real examples',
'Guesses at what went wrong, not diagnoses',
],
yes: [
'Names shots, ratios, milk temps confidently',
'Adjusts grind when shots pull wrong',
'Consistent under pressure -- has a real system',
'Catches problems before they hit the customer',
],
strongYes: [
'Trained others in coffee craft',
"Raised the café's standard when they arrived",
'Can diagnose an espresso machine issue',
'Talks about the chemistry, not just the routine',
],
},
{
criterionLabel: 'Speed during peak service',
no: [
'Describes panic, not composure',
'Blames others or the machine when busy',
'No memory of managing a rush well',
'Slows down and the queue grows',
],
maybe: [
"Managed a rush but wasn't leading it",
'Speed is okay, quality slips under pressure',
'Reactive more than proactive',
'Some anxiety visible in how they tell the story',
],
yes: [
'Has a station system that holds up in a rush',
'Keeps quality consistent even when slammed',
'Calm voice when describing their busiest shifts',
'Prioritises the right things automatically',
],
strongYes: [
'Others follow their lead during a rush',
'Has built or improved a workflow others now use',
'Describes their busiest day without stress in their voice',
'Queue management is something they actively do',
],
},
{
criterionLabel: 'Customer connection and warmth',
no: [
"Can't recall a specific customer interaction",
'Treats customers as orders, not people',
"Difficult customer stories end with 'I just smiled'",
'No awareness of regulars or repeat patterns',
],
maybe: [
'Friendly but surface-level -- no real examples',
"Handles complaints when they come, doesn't prevent them",
'Regulars are a nice idea but no specific examples',
'Script-like in how they describe customer interactions',
],
yes: [
'Remembers regulars and their orders',
"Reads a customer's mood before they speak",
'Has a real story of turning a complaint into a good outcome',
'Keeps service warm even at volume',
],
strongYes: [
'Regulars ask for them by name',
'Has retained customers because of how they were treated',
'Proactively changed something to improve the customer experience',
'Sees relationship-building as part of the job, not extra',
],
},
{
criterionLabel: 'Learning and following processes',
no: [
'Blames teammates when a shift went wrong',
'No ownership of their part in a team breakdown',
'Stories always cast them as the only capable one',
"Doesn't communicate on handover or during a shift",
],
maybe: [
"Works alongside the team, doesn't lead in it",
'Some tension in team stories -- unclear who caused it',
'Communicates when forced, not by default',
'Okay handover habits but inconsistent',
],
yes: [
'Named a teammate when describing a successful shift',
"Flagged something for the next person's shift",
'Handled a team disagreement without escalating',
"Turns up on time and tells people when they can't",
],
strongYes: [
'The one teammates call when a shift falls apart',
'Has covered shifts proactively without being asked',
'Improved handover or comms on their own initiative',
"Team runs better when they're on",
],
},
{
criterionLabel: 'Cleanliness and hygiene standards',
no: [
'Cleans up at the end, not during',
"Can't describe their cleaning routine at the machine",
'Vague on milk temp, cross-contamination, or fridge rotation',
"Treats food safety as someone else's job",
],
maybe: [
"Follows the routine when asked, doesn't own it",
'Some habits, no system',
"Mentions hygiene generally, can't name specifics",
'Cleans the station when it suits them',
],
yes: [
'Has a machine cleaning routine they run unprompted',
'Knows milk temps, rotation, and cross-contamination rules',
'Resets their station after every rush automatically',
'Can recall a time they caught a hygiene issue',
],
strongYes: [
'Set up or improved a cleaning schedule for the team',
'Has flagged a food safety issue formally',
'Cleanliness is something they enforce, not just practise',
'Can explain why the standards matter, not just what they are',
],
},
],
complianceGatesMeta: [
{ label: 'Food handling certificate', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 03: Kitchen Hand ──────────────────────────────────────────────────
kitchenHand: {
anchors: [
{
criterionLabel: 'Work rate and physical reliability',
no: [
'Describes slowing down when tired',
'Waits to be told what to do next',
'Shift stories are vague or short',
"Can't describe keeping up during a rush",
],
maybe: [
'Works hard when supervised',
"Gets through the shift, not driving it",
"Mentions being tired but doesn't explain how they managed",
'Pace is reactive, not consistent',
],
yes: [
'Describes a real rush and how they managed it physically',
'Picks up the next job without being asked',
'Works at pace for the full shift, not just the start',
'Has worked physically demanding jobs consistently',
],
strongYes: [
'Sets the pace others follow',
'Has done split shifts or double shifts without dropping standards',
'Proactively managed their energy across a long shift',
'Physical resilience is something they take pride in',
],
},
{
criterionLabel: 'Hygiene and food safety',
no: [
'Cleans only when told to',
"Can't describe basic hygiene rules",
"Treats food safety as someone else's job",
'No awareness of cross-contamination or temperature',
],
maybe: [
'Follows hygiene rules when prompted',
'Some awareness, no habits',
"Has done food handling training but can't recall specifics",
'Cleans at the end, not during',
],
yes: [
'Names food safety rules they actually follow',
'Cleans as they go without prompting',
'Has spotted a hygiene issue and flagged it',
'Knows why hygiene matters, not just what to do',
],
strongYes: [
'Has reported or fixed a food safety issue formally',
'Sets the hygiene standard others follow',
'Can explain cross-contamination and temperature rules clearly',
'Hygiene is something they enforce on themselves and the team',
],
},
{
criterionLabel: 'Team awareness and communication',
no: [
"Can't describe contributing to a team outcome",
'Just does their bit and disconnects',
'No communication with chefs or other kitchen staff',
'Team stories always have others at fault',
],
maybe: [
"Works alongside the team, doesn't engage with it",
'Communicates when asked, not proactively',
'Some friction stories -- unclear whose fault',
'Awareness of the team as concept, not in practice',
],
yes: [
'Named a specific teammate in a positive story',
'Communicated clearly under pressure',
'Flagged something to the chef without being asked',
"Works with the flow of the kitchen, not against it",
],
strongYes: [
"Kitchen runs more smoothly when they're on",
'Has helped a struggling teammate mid-shift',
'Proactively improved communication between stations',
'Seen by chefs as an extension of the team, not support',
],
},
{
criterionLabel: 'Initiative during quiet periods',
no: [
'Stands around when quiet',
'Waits to be given the next task',
'No examples of self-directed activity',
'Quiet periods are breaks in disguise',
],
maybe: [
'Does something when quiet, but only obvious tasks',
'Has helped out occasionally without being asked',
"Mentions looking around but doesn't describe acting",
'Initiative in isolated incidents, not a pattern',
],
yes: [
'Describes specific things they do in a quiet moment',
'Restocks, cleans ahead, preps without prompting',
'Has a mental list of what needs doing beyond the obvious',
'Quiet time is productive, not passive',
],
strongYes: [
'Built a system or routine others now follow in quiet periods',
"Found work that wasn't on anyone's list and did it",
'Has flagged inefficiencies in quiet moments',
"Kitchen is noticeably better-prepared when they're on",
],
},
{
criterionLabel: 'Reliability and coachability',
no: [
'History of no-shows or late starts',
'Gets defensive when corrected',
'Blames others when things go wrong',
"Can't recall accepting feedback well",
],
maybe: [
'Mostly shows up, occasional unexplained gaps',
'Accepts feedback but slowly and reluctantly',
'Some defensiveness in correction stories',
'Reliable in good weeks, variable otherwise',
],
yes: [
'Consistent attendance with clear reasons for any gaps',
'Recalls a specific time feedback improved how they worked',
"Doesn't get defensive when corrected",
"Turns up on time and tells people when they can't",
],
strongYes: [
'Sought out feedback proactively',
"Changed a long-held habit based on someone else's input",
'Zero pattern of unreliability across multiple roles',
'Others rely on them because they always show up',
],
},
],
complianceGatesMeta: [
{ label: 'Food handling certificate', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 04: Chef / Cook ───────────────────────────────────────────────────
chef: {
anchors: [
{
criterionLabel: 'Kitchen production under pressure',
no: [
"Can't describe their cooking style or speciality",
'Vague on technique under questioning',
'Has cooked but never led a section or service',
'Standards are aspirational, not demonstrated',
],
maybe: [
'Competent at the basics, unconfident on specifics',
'Some experience of running a section, not regularly',
"Can describe what they can cook, not how well",
"Mentions training but can't demonstrate the outcome",
],
yes: [
'Names specific dishes, techniques, and standards they hold',
'Has run a section or service without supervision',
'Catches quality issues before they reach the pass',
'Has cooked the same dish consistently across many services',
],
strongYes: [
'Has trained or mentored other cooks',
"Developed a dish or menu that improved the kitchen's output",
"Holds a standard others in the kitchen aspire to",
'Has worked at a level above what this role requires',
],
},
{
criterionLabel: 'Food quality and consistency',
no: [
'Panics in their service stories',
'Lets the board pile up without adjusting',
'Blames front of house or other cooks when service breaks',
'Loses quality when busy',
],
maybe: [
"Gets through service but not composedly",
'Adjusts plans but slowly and reactively',
"Keeps their head down, doesn't read the wider service",
"Some awareness of others' pressure, no action on it",
],
yes: [
'Describes a hard service calmly',
'Reads the board and adjusts before it becomes a problem',
'Communicates clearly with front of house under pressure',
'Quality holds even when the kitchen is in the weeds',
],
strongYes: [
"Stepped up when the service was at risk of falling apart",
'Has covered for an absent colleague mid-service without losing standard',
'Others on the team stayed calm because they stayed calm',
'Service runs better because of how they read and communicate',
],
},
{
criterionLabel: 'Team leadership and communication',
no: [
'Blames the brigade when service fails',
"No examples of raising someone else's standard",
'Creates tension in team stories',
'Takes credit, deflects blame',
],
maybe: [
"Works within the team but doesn't improve it",
"Has guided someone once but it's not a pattern",
"Tension in team stories -- partial ownership of their role in it",
'Aware of culture issues but did not act',
],
yes: [
'Named a specific time they helped a struggling colleague',
'Takes ownership when their section affects the wider service',
'Honest about a time they contributed to a team problem',
'Improves the person next to them by being there',
],
strongYes: [
'Has lifted the standard of a whole section or kitchen',
'Has had difficult conversations about standards without losing the relationship',
'New cooks perform better after working alongside them',
'Culture is something they actively build, not just benefit from',
],
},
{
criterionLabel: 'Health and safety compliance',
no: [
'Treats HACCP or temperature rules as overhead',
"Can't name a compliance issue they've managed",
'Passes food safety responsibility to others',
'Vague or dismissive on allergen handling',
],
maybe: [
"Follows food safety rules, doesn't own them",
"Some awareness of allergens, no protocols named",
"Has worked in compliant kitchens but at arm's length",
"Compliance is for the head chef, in their view",
],
yes: [
'Names HACCP, temperature logs, and allergen controls they personally manage',
'Has caught and acted on a food safety issue',
'Checks in with front of house on allergens before service',
"Sees compliance as their responsibility, not the manager's",
],
strongYes: [
'Set up or improved compliance systems in a previous kitchen',
'Has trained staff on food safety protocols',
'Reported a supplier or process issue through the right channel',
'Kitchen compliance improved measurably because of their involvement',
],
},
{
criterionLabel: 'Menu knowledge and adaptability',
no: [
'Defensive when questioned on technique',
'No examples of changing how they work based on feedback',
'Positions themselves as already fully formed',
"Can't recall a time a colleague or chef improved their approach",
],
maybe: [
'Accepts feedback formally but slowly integrates it',
'One example of coachability, not a pattern',
'Responds better to feedback from seniors than peers',
'Learning is something they do in courses, not day-to-day',
],
yes: [
'Recalls a specific time feedback changed their approach',
'Actively seeks input from head chefs or peers',
'Has integrated criticism and improved without prompting',
'Treats every kitchen as a place to learn, not just work',
],
strongYes: [
'Sought mentorship outside required training',
'Changed a long-held technique after being shown a better way',
'Has paid for or arranged their own further training',
'Sees coaching others as how they develop themselves',
],
},
],
complianceGatesMeta: [
{ label: 'Food Safety certificate', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 05: Café Manager ──────────────────────────────────────────────────
cafeManager: {
anchors: [
{
criterionLabel: 'Managing service under pressure',
no: [
'Managed people but not systems or standards',
"Cannot describe their daily opening or closing routine",
"Standards are aspirational -- can't name what they actually hold",
"Has managed a café on paper; not in practice",
],
maybe: [
"Follows the owner's systems, hasn't built their own",
'Some operational knowledge, thin on detail',
'Manages the team, delegates standards to others',
'Has had operational responsibility but not ownership',
],
yes: [
'Names their own standard for coffee, food, cleanliness, service',
'Describes their daily routine with real specificity',
'Has improved an operational system in a previous role',
'Holds their team to a standard and can describe it',
],
strongYes: [
"Built a café's operational systems from scratch or rebuilt them",
'Has turned around a café that was underperforming operationally',
"Others describe their shift as the benchmark",
"The café runs the same whether they're in or not -- because of systems they created",
],
},
{
criterionLabel: 'Holding standards with people you know',
no: [
'Staff management is telling people what to do',
'No examples of developing a team member',
'High turnover on their team with no ownership of it',
"Has managed people they didn't recruit or onboard",
],
maybe: [
'Manages performance reactively, not proactively',
"One development story but not a pattern",
'Understands coaching in theory, inconsistent in practice',
'Team turnover is okay but not great -- no clear reason why',
],
yes: [
'Can name someone they developed and what changed',
'Has had a difficult performance conversation and resolved it',
'Onboarding is something they think about and design',
'Staff stay because they feel supported, not just paid',
],
strongYes: [
'A team member was promoted because of their development',
'Has built a training system others now use',
'Has retained staff in a high-turnover environment through genuine leadership',
'Staff call them when they have problems, not just when they have questions',
],
},
{
criterionLabel: 'Staff rostering and reliability',
no: [
'Builds the roster around who is available, not who is needed',
"Can't describe their rostering process or tools",
'Has handed roster responsibility to someone else',
'No examples of managing coverage under pressure',
],
maybe: [
'Manages the roster adequately in normal weeks',
'Adjusts when someone calls in sick but reactively',
'Some awareness of labour cost but not actively managed',
'Roster drama is a pattern they acknowledge but cannot explain',
],
yes: [
'Builds the roster around the business need first, preferences second',
'Has managed a short-staffed service and kept standards',
'Tracks labour as a cost and adjusts rostering accordingly',
'Has had the hard conversation about availability with a staff member',
],
strongYes: [
'Roster is a management tool, not an admin task in their view',
'Has rebuilt a roster that was creating coverage or cost problems',
'Others follow their rostering approach as the team standard',
"Can describe their labour cost % and what moves it",
],
},
{
criterionLabel: 'Customer experience ownership',
no: [
'Customer complaints are handled by whoever is available',
'No system for feedback or recovery',
"Doesn't see themselves as responsible for the customer's whole experience",
"Experience is the barista's job, not the manager's",
],
maybe: [
"Responds to complaints well, doesn't prevent them",
"Some awareness of the customer journey, not systematic",
'Has improved one aspect of the customer experience',
'Friendly café, but not by design',
],
yes: [
'Has a complaint resolution approach they own',
'Has changed something based on customer feedback',
'Thinks about the full customer journey, from arrival to departure',
'Their shift has a different feel to others -- intentionally',
],
strongYes: [
"Built or redesigned a café's customer experience deliberately",
'Has turned regulars into advocates for the business',
'Has a story of recovering a serious complaint into a loyal customer',
'Customer experience is the business, in their view -- not a side task',
],
},
{
criterionLabel: 'Financial awareness and cost control',
no: [
'Knows the cafe is profitable or not but cannot explain why',
'Has never looked at a P&L or weekly sales report',
"Treats financial management as the owner's job",
"No awareness of food cost %, labour %, or wastage targets",
],
maybe: [
'Aware of food cost and labour as concepts, not actively managing them',
'Has looked at the numbers when asked, not by habit',
'Some understanding of wastage as a cost, no system to manage it',
'Can read a report but cannot act on what it shows',
],
yes: [
"Names the ratios they watch (food cost %, labour %, wastage)",
'Has identified and reduced a cost in a previous role with a specific example',
'Reviews sales and labour data weekly and adjusts decisions accordingly',
'Understands how their daily decisions affect the P&L',
],
strongYes: [
'Has materially improved margin in a previous role and can describe how',
'Uses financial data to drive rostering, ordering, and pricing decisions',
'Reports to owners on financial performance with confidence and specifics',
'Financial awareness is how they think about running a cafe, not a separate task',
],
},
],
complianceGatesMeta: [
{ label: "NZ Manager's Certificate (licensed venues only)", level: 'conditional' },
{ label: 'Food Safety certificate', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 06: Retail Assistant ──────────────────────────────────────────────
retailAssistant: {
anchors: [
{
criterionLabel: 'Customer service approach',
no: [
'Sells by showing, not advising',
"Can't describe learning a product range",
'No examples of helping a customer find the right product',
'Treats all customers as the same type of buyer',
],
maybe: [
"Knows the basics of the range, not the depth",
"Has recommended products but can't describe how they chose",
'Learning the product was passive, not active',
'Some customer advisory moments, not consistent',
],
yes: [
'Actively learned a product range and can describe how',
'Asks customers questions before recommending',
"Has matched a product to a customer's need with a real example",
'Knows when to escalate to a specialist',
],
strongYes: [
'Become the go-to on a product category in a previous role',
'Converted a browser into a buyer through product knowledge',
'Has trained others on a product range',
'Product knowledge is something they build proactively, not by accident',
],
},
{
criterionLabel: 'Product knowledge and enthusiasm',
no: [
'Treats all customers the same regardless of cues',
"Complaint handling is 'I got the manager'",
'No story of reading what a customer actually needed',
'Service is a process, not a judgment',
],
maybe: [
'Friendly and responsive, not proactive',
"Handles complaints when they arise, doesn't prevent them",
'Some examples of reading a customer, not consistent',
"Recalls service interactions but not the customer insight behind them",
],
yes: [
'Describes adjusting their approach for different customers',
'Has recovered a complaint without manager escalation',
'Reads tension or indecision in a customer and responds to it',
'Customers come back to them specifically',
],
strongYes: [
'Regulars ask for them by name or wait for their shift',
'Has identified a pattern in customer complaints and flagged it',
'Converted a difficult interaction into a positive review or repeat visit',
"Service is genuinely their thing, not just a job requirement",
],
},
{
criterionLabel: 'Reliability and punctuality',
no: [
"Presentation is someone else's job",
'Has no description of their role in stock management',
"Doesn't notice when the store is out of order",
'Fills shelves when asked, not before',
],
maybe: [
"Follows the standard when told, doesn't own it",
'Some awareness of visual merchandising, not a practice',
'Fills and tidies on shift but reactively',
'Stock management is inventory, not presentation in their mind',
],
yes: [
'Has a routine for checking and filling stock',
'Notices presentation issues and addresses them without prompting',
'Has flagged a stock issue (shrinkage, mislabelling, expired goods)',
'Understands why standards matter to the customer experience',
],
strongYes: [
"Improved a section's visual standard on their own initiative",
'Caught a stock discrepancy that saved the store money',
'Has trained others on stock and presentation standards',
'The store section they own always looks better than the rest',
],
},
{
criterionLabel: 'Cash handling and accuracy',
no: [
'Has used a till but recalls no specific incidents',
'No sense of responsibility for accuracy',
"Cash handling is just counting -- no awareness of the stakes",
'No experience resolving a transaction error',
],
maybe: [
'Accurate most of the time, occasional variances',
"Has handled cash but not end-of-day reconciliation",
"Knows how to process a return but not the policy behind it",
'Some experience with EFTPOS and cash, not both regularly',
],
yes: [
'Describes their accuracy habits at the till',
'Has resolved a transaction error calmly and correctly',
"Knows the return/refund policy and applies it consistently",
"End-of-day is something they own, not just observe",
],
strongYes: [
'Zero variance record across a significant period',
'Has trained others on till procedures',
'Has flagged a policy issue or system error that was affecting transactions',
'Transaction accuracy is something they take personal responsibility for',
],
},
{
criterionLabel: 'Team contribution and attitude',
no: [
'Pattern of late starts or unexplained absences',
'Team stories always cast others as the problem',
"Doesn't consider how their absence affects the team",
'Covers shifts only when it suits them',
],
maybe: [
'Shows up consistently, not proactively reliable',
'Team contribution is doing their own job',
'Covers occasionally, not a pattern',
'Reliability varies with how much they like the role',
],
yes: [
'Consistent attendance with clear reasons for any gaps',
'Has covered a shift for a teammate proactively',
'Contributes to the team beyond their own duties',
'Others depend on them being there',
],
strongYes: [
"The manager's first call when they need cover",
'Has held a team together during a busy period with a short roster',
'Goes beyond their job description as a matter of habit',
'Reliability is something they treat as a professional standard, not just a rule',
],
},
],
complianceGatesMeta: [
{ label: 'Right to work in NZ', level: 'hard' },
{ label: 'Age verification awareness (if alcohol/tobacco sold)', level: 'conditional' },
],
},

// ─── Role 07: Retail Manager ────────────────────────────────────────────────
retailManager: {
anchors: [
{
criterionLabel: 'Sales performance and commercial ownership',
no: [
"Manages a team but not the numbers",
"Can't describe conversion rate, ATV, or their store's targets",
"Sales is the team's job, not their direct accountability",
'Vague on what drove results in their previous role',
],
maybe: [
"Monitors sales, hasn't actively driven improvement",
'Knows the targets, limited evidence of reaching them',
'Some understanding of conversion and ATV, not applied',
"Reports results, hasn't interrogated them",
],
yes: [
'Describes specific actions they took to hit a sales target',
'Knows their conversion rate and what moves it',
'Has identified and fixed an underperforming product or section',
"Owns the store's commercial result, not just the team's effort",
],
strongYes: [
"Has materially improved a store's sales performance with specific numbers",
'Built and executed a sales improvement plan',
'Knows their trade area, competitors, and the margin structure',
'Thinks commercially about every decision they make in the store',
],
},
{
criterionLabel: 'Team leadership and performance management',
no: [
"Performance management is HR's job",
'Has managed a team but avoided difficult conversations',
'High turnover with no self-reflection on why',
"No examples of developing a team member's performance",
],
maybe: [
'Manages reactively -- addresses problems when they escalate',
'Development happens informally, no structure',
"Has had one difficult conversation, not a pattern of managing well",
"Team runs okay; hasn't improved under their leadership",
],
yes: [
'Has had a structured performance conversation with a real outcome',
'Can describe someone who improved under their management',
'Onboards new staff with a deliberate plan',
'Turnover is lower because of how they treat people',
],
strongYes: [
'Has exited someone fairly and the team was stronger after',
'Built a development pathway for a high-potential team member',
'Has restructured a team and improved performance measurably',
"Others describe them as the best manager they've had",
],
},
{
criterionLabel: 'Store operations and standards',
no: [
"Operational standards are someone else's concern",
"Can't describe their routine for opening or closing",
"Store presentation is managed when they're in, not when they're not",
"Systems are followed, not owned",
],
maybe: [
"Follows the franchisor or owner's systems, hasn't built their own",
'Operations are stable but not improving',
"Reacts to operational problems, doesn't prevent them",
"Standards in their section are good; rest of store is variable",
],
yes: [
'Has a consistent opening and closing routine they enforce',
'Has built or improved an operational system (rostering, stocktake, returns)',
"Standards hold when they're not present -- because they've built that culture",
'Has identified an operational inefficiency and fixed it',
],
strongYes: [
'Has taken a store from below standard to consistent performance',
'Built operational systems adopted by other stores or by the owner',
'Operational excellence is a personal value, not a KPI',
"The store runs the same on their day off as when they're there",
],
},
{
criterionLabel: 'Inventory and loss prevention',
no: [
'Stocktake is an admin exercise, not a management tool',
"Shrinkage is a head office problem",
"Can't describe their process for investigating a stock discrepancy",
"Loss prevention is security's job",
],
maybe: [
"Does stocktakes, doesn't use them to manage",
'Aware of shrinkage as a concept, not managing it actively',
"Has flagged a discrepancy but didn't investigate",
"Inventory is someone else's primary responsibility",
],
yes: [
'Uses stocktake data to spot patterns and act',
'Has investigated and resolved a stock discrepancy',
'Has reduced shrinkage with a specific action or system',
"Knows the store's shrinkage rate and what drives it",
],
strongYes: [
'Has built a loss prevention system that others now use',
'Has caught and managed internal theft through investigation, not assumption',
'Has materially reduced shrinkage across a store or department',
'Inventory accuracy is a commercial priority, not a compliance exercise in their view',
],
},
{
criterionLabel: 'Customer experience and complaint resolution',
no: [
'Customer complaints are escalated away, not owned',
"Experience is the floor team's job",
'Has managed a complaint with the customer, not with the team',
'No systemic approach to the customer experience',
],
maybe: [
"Handles escalated complaints well, doesn't prevent them",
'Some awareness of how the team creates the experience, not systematic',
'Has changed something based on a complaint -- isolated not repeated',
'Friendly store, not by design',
],
yes: [
'Has resolved a serious complaint and kept the customer',
'Has changed a team behaviour based on customer feedback',
'Sees the customer experience as a management accountability',
'Has used NPS or feedback data to improve something specific',
],
strongYes: [
'Turned a complaint pattern into a systemic improvement',
'Customers wrote in to praise the store by name under their leadership',
'Built a complaint resolution process that others now follow',
'Customer advocacy is something they deliberately drive, not just measure',
],
},
],
complianceGatesMeta: [
{ label: 'Right to work in NZ', level: 'hard' },
{ label: 'Age-restricted products (if applicable)', level: 'conditional' },
],
},

// ─── Role 08: Office Administrator ─────────────────────────────────────────
officeAdministrator: {
anchors: [
{
criterionLabel: 'Organisation and attention to detail',
no: [
'No personal system for managing competing tasks',
'Drops things when interrupted',
"Relies on others to tell them what's next",
'Reactive only -- no planning ahead',
],
maybe: [
'Has a system but it breaks down under pressure',
'Manages tasks okay, inconsistent when volume increases',
'Some planning, mostly reactive',
'Has missed a task occasionally without catching it early',
],
yes: [
'Describes their actual system (tool, list, routine)',
'Plans ahead for recurring tasks',
'Manages interruptions without losing track of outstanding work',
'Has never missed a deadline because of a system failure',
],
strongYes: [
'Built an admin system others in the office now use',
'Their absence requires documented handover because things would fall through',
'Has managed a complex administrative project without reminders',
"Other people's tasks get captured in their system too",
],
},
{
criterionLabel: 'Written and verbal communication',
no: [
'Relies on others to catch errors',
"Doesn't read back what they've produced",
"Treats detail work as someone else's job",
"Has made a significant error they didn't catch",
],
maybe: [
'Mostly accurate, occasional errors they catch',
"Has a checking habit but it's inconsistent",
'Accurate on familiar tasks, errors on unfamiliar ones',
'Proofing is a final step, not built into the process',
],
yes: [
'Has a specific checking habit they use before sending anything',
"Catches others' errors as well as their own",
'Describes a time an accuracy habit prevented a real problem',
'Accuracy is faster than correcting -- they have that mindset',
],
strongYes: [
'Has zero significant error record across a sustained period',
'Built a checking system for a document or process that reduced errors organisation-wide',
'Caught an error that would have had serious consequences',
'Detail is something they see as a professional standard, not a requirement',
],
},
{
criterionLabel: 'Software and systems proficiency',
no: [
"Communication is email and phone -- no awareness of stakeholder management",
"Can't describe managing expectations when something goes wrong",
'Reactive communicator -- waits to be chased',
'Escalates everything rather than judging what needs escalation',
],
maybe: [
"Responsive when contacted, doesn't initiate",
'Has managed a stakeholder issue once, not a pattern',
'Email communication is okay, verbal less confident',
"Communicates within their own team, not across the organisation",
],
yes: [
'Proactively updates people when something has changed',
'Has managed competing requests from multiple senior stakeholders',
'Knows when to escalate and when to resolve',
"Tailors their communication style to who they're talking to",
],
strongYes: [
'Is the trusted interface between senior staff and the wider organisation',
'Has de-escalated a situation through communication that others had made worse',
'Manages expectations proactively rather than apologising reactively',
'Senior leaders rely on them as the point of contact for the office',
],
},
{
criterionLabel: 'Prioritisation under pressure',
no: [
'Shares information because it was interesting, not because it was appropriate',
'No clear framework for what should stay confidential',
"Has disclosed something they shouldn't have",
'Treats confidentiality as a rule, not a value',
],
maybe: [
"Knows what's confidential but hasn't been tested on it",
"Mostly appropriate, one story that's borderline",
'Understands the principle but applies it unevenly',
'Escalates confidentiality questions but slowly',
],
yes: [
'Has declined to share information and explained why clearly',
'Can describe a moment where their discretion protected someone',
'Treats all personnel and business information as confidential by default',
'Has escalated a confidentiality concern before it became a problem',
],
strongYes: [
'Handled highly sensitive information (HR, financial, legal) without incident across a sustained period',
'Has proactively identified a gap in how confidential information was being handled and raised it',
'Their discretion is relied upon by senior leaders for the most sensitive matters',
'Confidentiality is a personal professional standard, not an instruction they follow',
],
},
{
criterionLabel: 'Proactivity and initiative',
no: [
"Follows the process as given, doesn't improve it",
'No examples of identifying a better way',
"Improvement suggestions are someone else's role",
"Comfortable with the status quo even when it's inefficient",
],
maybe: [
"Has noticed an inefficiency but didn't act on it",
'Made one improvement when asked, not proactively',
"Some awareness of better tools or processes, hasn't implemented them",
"Mentions things that could be better but leaves it there",
],
yes: [
'Has identified and implemented a process improvement without prompting',
"Suggested a system or tool that improved the team's efficiency",
'Describes a process they inherited and made their own',
'Efficiency is something they return to as a value, not a one-off',
],
strongYes: [
'Has materially reduced time or errors in an admin process with a system they designed',
"Their improvements are still running in an office they've left",
'Has trained others on a better process they created',
'Process thinking is how they approach any new responsibility',
],
},
],
complianceGatesMeta: [
{ label: 'Right to work in NZ', level: 'hard' },
{ label: 'Background check (if required)', level: 'conditional' },
],
},

// ─── Role 09: Warehouse / Storeperson ──────────────────────────────────────
warehouseStoreperson: {
anchors: [
{
criterionLabel: 'Pick, pack, and dispatch accuracy',
no: [
'Relies on others to catch picking errors',
'No personal checking habit before dispatch',
"Accuracy is the system's job, not theirs",
"Has caused a dispatch error and doesn't own it",
],
maybe: [
'Mostly accurate, occasional errors they catch late',
'Checking is a final step, not part of the process',
"Knows the stakes of an error but hasn't changed their process",
'Speed is prioritised over accuracy when things are busy',
],
yes: [
'Has a checking habit before confirming pick or dispatch',
'Recalls a specific time they caught an error before it left the building',
'Accuracy and speed are not in conflict in how they work',
'Their error rate is genuinely low and they know why',
],
strongYes: [
'Zero significant dispatch error record in a previous role',
"Has caught another person's error before it left the building",
'Has suggested or implemented a checking process others now use',
'Accuracy is a professional standard they hold independently of supervision',
],
},
{
criterionLabel: 'Health, safety, and housekeeping',
no: [
"H&S is management's job",
"Can't describe a specific hazard they've spotted",
'Housekeeping is end-of-day, not continuous',
'No sense of their own responsibility for a safe site',
],
maybe: [
"Follows safety rules, doesn't own them",
'Has noticed a hazard but waited for someone else to act',
'Keeps their area clean when prompted',
'H&S in theory, variable in practice',
],
yes: [
'Names a specific hazard they spotted and acted on',
'Daily housekeeping is a habit, not a task',
'Has flagged a near-miss or unsafe condition formally',
"Understands how their actions affect others' safety",
],
strongYes: [
'Has stopped work because a site was unsafe',
'Has improved a safety practice across a team, not just their own',
'Near-miss reporting is something they champion, not avoid',
"H&S is how they think about their work, not an overlay on it",
],
},
{
criterionLabel: 'Physical reliability and sustained output',
no: [
'Pace drops as the shift progresses',
"Can't describe maintaining output across a full shift",
'Physical demands of previous roles were lighter than this one',
'Works hard when supervised, otherwise steady',
],
maybe: [
"Gets through the shift, not driving it",
'Maintains pace on most shifts, inconsistent under pressure',
'Some history of physical roles, not all at warehouse intensity',
'Good at the start, tires toward the end',
],
yes: [
'Has sustained physical output across demanding shifts',
'Manages their energy deliberately -- not just their pace',
'Has worked consistently through high-volume periods',
'Physical work is something they actively choose',
],
strongYes: [
'Has maintained top-quartile output through an entire peak season',
"Has done shifts others found difficult without complaint or drop in standard",
"Physical reliability is something their manager has specifically commented on",
'Treats physical output as a professional standard',
],
},
{
criterionLabel: 'Inventory management and stock control',
no: [
'Stocktaking is counting, not managing',
'No awareness of FIFO, rotation, or location accuracy',
"Inventory discrepancies are someone else's problem",
'Has worked in a warehouse but not with stock management responsibility',
],
maybe: [
"Does stocktakes, doesn't use them to manage",
"Knows FIFO in theory, applies it unevenly",
"Aware of location accuracy but hasn't owned it",
"Inventory is part of their role but not something they've improved",
],
yes: [
'Applies FIFO consistently and can describe why',
'Has investigated and resolved a stock discrepancy',
'Understands how inventory accuracy affects operations downstream',
'Has flagged a location or count issue before it became a problem',
],
strongYes: [
"Has improved a warehouse's stock accuracy with a practice they implemented",
'Zero significant stocktake variance in a previous role',
'Has trained others on inventory management practices',
'Treats inventory accuracy as a commercial responsibility, not a task',
],
},
{
criterionLabel: 'Team communication and reliability',
no: [
'Shows up when it suits them',
"Doesn't consider how their absence affects the shift",
'Communication with teammates is minimal',
'Reliability is good in easy periods, variable in hard ones',
],
maybe: [
'Mostly shows up, occasional gaps with plausible reasons',
'Communicates when they need something, not proactively',
'Reliable in steady periods, less so in demanding ones',
'Team contribution is doing their own job',
],
yes: [
'Consistent attendance with clear reasons for any gaps',
'Communicates proactively when something will affect the shift',
'Has covered or supported a struggling teammate',
'Reliability is consistent across easy and demanding periods',
],
strongYes: [
"The shift supervisor's first call when cover is needed",
'Has held a team together during a peak period short on staff',
'Communication is something they initiate, not just respond to',
'Zero pattern of unreliability across multiple roles',
],
},
],
complianceGatesMeta: [
{ label: 'Forklift licence (if required)', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 10: Medical Receptionist ─────────────────────────────────────────
medicalReceptionist: {
anchors: [
{
criterionLabel: 'Patient-facing composure and empathy',
no: [
'Treats patients like admin tasks',
"Can't describe adapting their approach for a distressed patient",
'Complaints are escalated away, not resolved',
'Clinical boundary awareness is absent',
],
maybe: [
'Friendly and calm in straightforward situations',
'Has managed a distressed patient once, not a pattern',
"Empathy is their self-description, not demonstrated in stories",
'Awareness of clinical context is thin',
],
yes: [
'Describes adjusting their approach for a patient who was anxious or upset',
'Has resolved a patient complaint without escalating',
'Understands the difference between administrative and clinical decisions',
'Warmth is consistent across the full waiting room, not just the easy ones',
],
strongYes: [
'Has de-escalated a genuinely difficult patient situation',
'Other staff or patients have specifically commented on how they made someone feel',
'Handles medical distress with calm that others in the role struggle to maintain',
'Has improved how the practice manages patient-facing situations',
],
},
{
criterionLabel: 'Confidentiality and information handling',
no: [
'Shared patient information because it was convenient, not appropriate',
'No framework for what stays confidential',
"Has disclosed something they now recognise was wrong",
"Treats the Privacy Act as someone else's concern",
],
maybe: [
"Knows what's confidential, hasn't been meaningfully tested",
'Appropriate in obvious situations, uncertain in edge cases',
'Escalates to the GP rather than reasoning through the decision',
'Privacy awareness is rule-following, not judgement',
],
yes: [
'Has declined a request for patient information and described how',
'Applies the minimum-necessary principle without being told',
'Has escalated a confidentiality concern before it became a problem',
'Handles records, calls, and conversations with consistent discretion',
],
strongYes: [
'Has managed a complex information request (ACC, insurance, third party) correctly',
'Has identified and raised a gap in how the practice handled patient information',
'Is the trusted point of contact for privacy questions in their current role',
'Privacy is a professional standard they hold independently of supervision',
],
},
{
criterionLabel: 'Multi-task management under pressure',
no: [
'One task at a time -- loses track when interrupted',
'Phones, patients, and admin are in competition in their stories',
'Drops things when the waiting room fills up',
'Pressure is visible in how they describe their busiest days',
],
maybe: [
'Manages most competing demands, occasional ball-drops',
'Has a system but it breaks in peak periods',
'Calm in familiar situations, anxious in novel ones',
"Multi-tasking is their weakness -- they acknowledge it",
],
yes: [
'Describes a specific system for managing phones, walk-ins, and admin simultaneously',
'Has managed a full waiting room and stayed organised',
'Knows what to prioritise and can explain the logic',
'Calm under pressure is a pattern, not a one-off',
],
strongYes: [
'Others come to them when the desk is chaotic because they stay calm',
'Has designed or improved a desk system during a busy period',
'Has never lost a thread under pressure across a sustained period',
"Pressure is when they're most useful, not most stretched",
],
},
{
criterionLabel: 'Clinical environment awareness',
no: [
"Doesn't distinguish between urgent and routine presentations",
'Has created risk by mishandling a clinical triage cue',
'Treats all patients at the front desk the same regardless of presentation',
'No awareness of how reception decisions affect clinical flow',
],
maybe: [
'Has worked in a medical practice but relies on clinicians for all triage cues',
'Some awareness of urgent presentations, inconsistently applied',
'Has escalated once appropriately, not a reliable pattern',
'Clinical awareness is reactive, not built-in',
],
yes: [
'Can describe the signs they watch for in a patient that would prompt escalation',
'Has escalated a patient appropriately and can describe what happened',
'Understands that reception is the front line of clinical safety',
'Has discussed a clinical situation with the GP and understood the outcome',
],
strongYes: [
"Has prevented a serious outcome by recognising a patient's presentation and escalating immediately",
"Clinical team relies on their triage instinct, not just their admin output",
"Has improved the practice's escalation protocol based on their experience",
'Sees their role as part of the clinical team, not parallel to it',
],
},
{
criterionLabel: 'Accuracy and records management',
no: [
'Records entry is routine data -- no awareness of the stakes',
'Has made entries without verifying',
"Errors in records are the system's problem",
'No checking habit before confirming a booking or record update',
],
maybe: [
'Mostly accurate, corrects own errors when found',
'Some checking habit, inconsistently applied',
"Knows the stakes but hasn't systematically changed how they work",
'Records management is their job but not their focus',
],
yes: [
'Has a personal checking habit before confirming any patient record',
'Has caught a records error before it created a clinical problem',
'Understands why accuracy in a patient record matters clinically',
'Records are not just admin -- they carry clinical significance in their view',
],
strongYes: [
'Zero significant records error in a sustained period',
'Has improved a records checking process in a previous practice',
'Has caught an error that would have had clinical consequences',
'Accuracy is a professional standard they hold independently of any system',
],
},
],
complianceGatesMeta: [
{ label: 'Privacy Act 2020 and Health Information Privacy Code 2020 awareness', level: 'hard' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 11: Sales Representative ─────────────────────────────────────────
salesRepresentative: {
anchors: [
{
criterionLabel: 'Pipeline and territory management',
no: [
"Reacts to leads, doesn't build a pipeline",
"Territory is 'who I visit' -- no strategic thinking",
"Can't describe their pipeline at a point in time",
'Activity is output, not structured toward results',
],
maybe: [
'Has a pipeline but manages it informally',
'Territory awareness exists, no plan behind it',
"Knows their current opportunities, can't project forward",
'Pipeline discipline breaks down when busy',
],
yes: [
'Describes their pipeline with real specificity (stage, value, next action)',
'Has a territory plan and can explain its logic',
'Reviews their pipeline weekly and adjusts',
'Activity is structured toward conversion, not just volume',
],
strongYes: [
'Has built a territory from scratch and hit target',
'Pipeline is their primary management tool, not a reporting exercise',
'Has identified underserved segments in a territory and captured them',
"Others ask how they manage their pipeline because it's evidently working",
],
},
{
criterionLabel: 'Conversion and closing',
no: [
'Conversion is luck or product strength, not skill',
'No close techniques they can name',
'Stalls at the decision stage without understanding why',
'Results are activity-based, not conversion-based',
],
maybe: [
'Closes when conditions are easy, struggles with genuine objections',
'Some conversion discipline, breaks down under pressure',
"Has closed deals but can't describe the technique",
'Results are okay, not driven by skill',
],
yes: [
'Describes a specific approach they use at the decision stage',
'Has converted a reluctant or complex buyer and can explain how',
'Handles objections without panic and can describe their approach',
'Conversion rate is something they track and improve',
],
strongYes: [
'Has converted a buyer who initially said no and kept the relationship',
'Conversion rate is top-quartile in their team or region',
'Has trained or coached others on closing',
'Closing is something they do actively, not wait for',
],
},
{
criterionLabel: 'Relationship development and retention',
no: [
'Relationships end at the sale',
"Account retention is someone else's job",
"Has lost a key account and can't explain why",
'No examples of growing an existing account',
],
maybe: [
'Friendly with customers, not strategically building the relationship',
'Has retained accounts but passively',
'Visits customers reactively, not on a plan',
'Relationship value is about being liked, not about business growth',
],
yes: [
'Has grown an existing account meaningfully',
'Plans their customer contact with commercial intent',
'Has retained a customer who was considering leaving',
"Customers trust them with problems they wouldn't tell other reps",
],
strongYes: [
'Has built accounts from small to anchor over multiple years',
"Customers specifically request them over competitors' reps",
'Has generated referrals or cross-sell from relationship capital',
'Account growth is their primary commercial strategy',
],
},
{
criterionLabel: 'Commercial and product knowledge',
no: [
'Sells on rapport, not knowledge',
"Can't describe the product's commercial value to the buyer's business",
'Relies on technical support for any substantive question',
'Product knowledge is surface-level and static',
],
maybe: [
"Knows the product well, can't explain value in business terms",
"Some commercial knowledge, not applied consistently",
"Has technical depth but translates it poorly to buyers",
"Product knowledge is up to date, competitive knowledge is thin",
],
yes: [
"Explains the product in terms of the buyer's business problem",
'Knows competitors and can articulate differentiation',
"Has used commercial insight to change a buyer's decision criteria",
'Product and market knowledge is part of how they prepare for every conversation',
],
strongYes: [
'Has won deals where they were not the lowest-cost option because of commercial knowledge',
"Buyers have changed their specification because of insights from this rep",
'Competitive knowledge is a genuine advantage in their hands',
'Commercial knowledge is something they actively maintain, not just rely on',
],
},
{
criterionLabel: 'Resilience and self-management',
no: [
'Rejection affects their next conversation',
'No reset mechanism between hard calls',
'Results-pressure creates visible anxiety',
"Self-management is not a thing they've thought about",
],
maybe: [
'Recovers from rejection but slowly',
'Has a rough system for dealing with a run of bad results',
'Some resilience, variable when the pressure is sustained',
'Self-management is reactive, not structured',
],
yes: [
'Describes their actual system for dealing with a lost deal',
'Has maintained activity and attitude through a poor quarter',
"Doesn't let a no affect the next conversation visibly",
"Resilience is something they've thought about and can describe",
],
strongYes: [
'Has led their team through a difficult period by maintaining energy',
'Lost a significant deal and immediately focused on what to learn from it',
'Has turned a consistent run of rejection into improved conversion through analysis',
'Self-management is how they treat sales as a profession',
],
},
],
complianceGatesMeta: [
{ label: 'Full NZ driver licence', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 12: Customer Service Representative ───────────────────────────────
customerServiceRep: {
anchors: [
{
criterionLabel: 'Customer resolution and ownership',
no: [
'Passes difficult issues to a supervisor',
'Closes a contact without confirming the customer is satisfied',
'Resolution is a process, not an outcome in their view',
"Can't describe what they do when they can't resolve something",
],
maybe: [
'Resolves most issues but escalates when emotional',
"Follows the process, doesn't adapt when the process fails",
'Some ownership, gives way when a customer pushes back',
"Closes the contact but doesn't follow up",
],
yes: [
'Has resolved a complex issue without escalating',
'Owns the outcome -- follows up to confirm resolution',
"Adapts the process when standard resolution doesn't work",
'Stays with a difficult contact until the issue is actually fixed',
],
strongYes: [
'Has resolved a contact that others had failed on',
'Changed a process because a resolution pattern kept failing',
'Has turned an angry customer into a satisfied one across a difficult interaction',
"Resolution is personal -- they don't consider a contact done until the customer agrees",
],
},
{
criterionLabel: 'Communication clarity and tone',
no: [
'Formal and rigid regardless of customer tone',
'Written communication has errors or is hard to follow',
"Can't adjust their language for different customers",
'Tone is scripted, not adaptive',
],
maybe: [
'Clear in simple situations, loses structure in complex ones',
'Adjusts tone occasionally but not consistently',
'Okay on phone, less confident in writing',
'Communication is adequate -- not a strength',
],
yes: [
'Describes adapting their language and tone for different customers',
'Written communication is clear and does the job without editing',
'Has explained a complex issue in simple terms with a real example',
'Tone shifts when the customer needs warmth, not just accuracy',
],
strongYes: [
'Customers have specifically commented on how clearly they communicated',
'Has redesigned a template or response that was creating confusion',
'Handles multi-channel contacts with consistent quality (phone, email, chat)',
'Communication is a professional skill they actively maintain',
],
},
{
criterionLabel: 'System and process accuracy',
no: [
'Data entry is a task, not a responsibility',
"Has made a record error and didn't own it",
'Process compliance is optional when the customer is difficult',
"Can't describe their accuracy habits",
],
maybe: [
'Mostly accurate, corrects own errors when found late',
'Follows the process in straightforward cases, shortcuts when busy',
'Some checking habit, inconsistently applied',
'Knows policy but applies it selectively',
],
yes: [
'Has a checking habit before confirming a customer record or commitment',
'Follows policy even when the customer pushes back',
'Has caught an error before it created a customer problem',
'Accurate under pressure, not just when things are easy',
],
strongYes: [
'Zero significant data or process error in a sustained period',
'Has identified a system issue that was causing recurring errors',
'Policy compliance is a professional value, not just a rule',
"Others check their work against this person's standard",
],
},
{
criterionLabel: 'Resilience under emotional pressure',
no: [
'Difficult contacts affect their next one',
'No reset mechanism between hard calls',
'Has said something in response to customer aggression that they now regret',
'Emotional pressure accumulates visibly through a shift',
],
maybe: [
'Recovers from a hard contact but slowly',
'Some mechanism for resetting, breaks down in sustained pressure',
'Aware of the need to manage their reaction, inconsistently successful',
'Good days and bad days are equally common',
],
yes: [
'Describes their actual reset mechanism between difficult contacts',
'Has maintained tone and professionalism through a sustained difficult period',
'Knows when to escalate for their own wellbeing and does so appropriately',
"Difficult contacts don't affect their next one -- and they can explain why",
],
strongYes: [
'Has led a team through a high-pressure period and maintained morale',
'Has handled the most difficult contact type in their environment without incident',
"Resilience is something they've thought about and built deliberately",
'Others come to them for advice on managing difficult customers',
],
},
{
criterionLabel: 'Initiative and process improvement',
no: [
"Follows the script, doesn't deviate",
"Has noticed a problem but left it for someone else",
"Process improvement is team leaders' job",
"Comfortable with the current state even when it's not working",
],
maybe: [
"Has flagged a problem once, not a pattern",
"Some ideas about improvement but hasn't acted on them",
"Initiative happens when it's encouraged, not independently",
"Mentions things that could be better but doesn't act",
],
yes: [
'Has improved something in the process or a template without being asked',
'Has flagged a pattern in contacts that led to a change',
'Goes beyond the immediate contact to prevent the next one',
'Improvement is something they return to as a professional habit',
],
strongYes: [
'Has designed or significantly improved a process others now use',
'Identified a systemic contact driver and had it fixed upstream',
'Their improvement ideas are actively sought by team leaders',
'Treats their contact centre role as a source of continuous improvement, not just task completion',
],
},
],
complianceGatesMeta: [
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 13: Hairdresser / Barber ─────────────────────────────────────────
hairdresser: {
anchors: [
{
criterionLabel: 'Technical skill and quality of work',
no: [
"Can't describe their range of services with specificity",
"Quality stories are vague -- 'I always do good work'",
'No examples of technical difficulty they navigated',
'Relies on the client to notice quality issues',
],
maybe: [
'Competent on standard services, uncertain on complex work',
'Some technical range, not versatile',
'Can describe their process but the examples are thin',
'Quality is generally good, inconsistent on challenging hair types or colours',
],
yes: [
'Names specific techniques (balayage, precision cut, fade) with confidence',
'Has managed a technically difficult request and can describe how',
'Catches quality issues before the client sees them',
'Consistent results across different hair types and styles',
],
strongYes: [
'Has a reputation for a specific technique others refer clients to them for',
'Has trained or mentored other stylists on technical skills',
'Produces results that generate referrals and repeat bookings',
'Technical range is something they actively expand',
],
},
{
criterionLabel: 'Client trust and honest communication',
no: [
"Tells the client what they want to hear, not what's achievable",
'No examples of managing expectation in a consultation',
"Has done work they knew wasn't right to avoid a difficult conversation",
"Client satisfaction is always the client's responsibility",
],
maybe: [
'Mostly honest, softens feedback when the client is committed',
'Some consultation skill, breaks down when the client pushes back',
"Has managed expectations once but it's not a consistent approach",
'Avoids conflict by agreeing, not by managing',
],
yes: [
"Has told a client their request wasn't achievable and explained why",
'Consultation is a structured part of every appointment, not an afterthought',
'Has redirected a client to a better outcome than they asked for',
'Clients trust their advice enough to give them creative freedom',
],
strongYes: [
'Clients have come back specifically because they were told the truth',
'Has managed a hair health conversation that required real honesty',
'Has retained a client who was disappointed initially because the communication was real',
'Honesty is their reputation -- clients refer others because of it',
],
},
{
criterionLabel: 'Client experience and retention',
no: [
'Clients are transactions -- no system for retention',
"Can't describe what they do to make a client come back",
"Rebooking is the client's initiative, not theirs",
'Client experience is about the service, not the relationship',
],
maybe: [
'Clients enjoy their appointments but rebook inconsistently',
'Has retained clients in easy circumstances, not difficult ones',
'Rebooking is mentioned but not a deliberate practice',
'Client experience is good -- not by design',
],
yes: [
'Rebooking is part of every appointment -- they initiate it',
'Has retained a client who was considering leaving or switching',
'Clients bring friends or family because of how they were treated',
'Client experience has a system behind it, not just a personality',
],
strongYes: [
'Has a strong personal clientele that has followed them between salons',
'Retention rate in previous role was measurably high',
'Generates referrals consistently -- can explain how',
'Client relationship is a long-term investment they actively manage',
],
},
{
criterionLabel: 'Time management and appointment discipline',
no: [
'Runs late regularly, blames the client or the previous appointment',
'No adjustment mechanism when running behind',
'Client experience suffers visibly when the schedule is tight',
"Time management is the salon's problem, not theirs",
],
maybe: [
'Mostly on time, late occasionally without a system to recover',
'Adjusts when very behind, not proactively',
"Time pressure creates quality shortcuts they're aware of",
'On time in easy weeks, variable in busy ones',
],
yes: [
'Manages their column proactively -- adjusts before falling behind',
'Has a technique for staying on time without sacrificing quality',
'Communicates proactively with waiting clients when running late',
'Schedule discipline is something they take personal responsibility for',
],
strongYes: [
'Never runs more than a few minutes late and can explain why',
"Has improved a salon's overall schedule adherence through their own discipline",
'Other stylists ask how they manage their column',
'Time management is a professional standard they hold independently',
],
},
{
criterionLabel: 'Salon team and professional conduct',
no: [
"Doesn't consider how their pace or conduct affects others in the salon",
'Team tension stories always have others at fault',
'Professional conduct is self-defined, not regulated',
'Has created a difficult dynamic in a previous salon',
],
maybe: [
"Gets on with the team, doesn't contribute to it",
'Some friction stories -- partial ownership',
'Professional conduct is fine in normal circumstances, variable under pressure',
'Team culture is nice to have, not something they build',
],
yes: [
'Has worked in a team salon and contributed to a positive environment',
'Handles professional conduct situations (lateness, standards, hygiene) directly but fairly',
'Has supported a struggling colleague without being asked',
'Salon culture is something they care about and contribute to',
],
strongYes: [
'Others describe working with them as a positive part of the job',
'Has mentored a junior stylist or helped a new team member settle in',
'Has navigated a difficult salon dynamic professionally and without drama',
"Sees team culture as their responsibility as much as management's",
],
},
],
complianceGatesMeta: [
{ label: 'NZ Certificate in Hairdressing (Level 4) or equivalent', level: 'hard' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 14: Support Worker / Care Worker ──────────────────────────────────
supportWorker: {
anchors: [
{
criterionLabel: 'Person-centred care and dignity',
no: [
'Talks about clients as tasks or conditions, not people',
'No examples of adapting their care approach for a specific client',
'Dignity in care is assumed, not described in practice',
"Clients' preferences are secondary to the routine",
],
maybe: [
'Caring in intention, task-focused in practice',
'Some person-centred examples, not consistent',
"Follows the care plan but doesn't build on it",
'Dignity is respected in normal circumstances, variable when pressured',
],
yes: [
"Describes adapting their approach to a specific client's preferences",
"Has advocated for a client's dignity or preference within the team",
'Care plan is a floor, not a ceiling -- they build on it with the client',
'Talks about what matters to the client, not just what needs to be done',
],
strongYes: [
"Has changed a client's care plan based on what they learned about their preferences",
"Has noticed a client's declining dignity and raised it formally",
'Clients have specifically said they prefer working with this person',
'Person-centred care is how they think about the work, not a framework they apply',
],
},
{
criterionLabel: 'Boundary management and professional conduct',
no: [
'Professional boundaries are a concept, not a practice',
'Has disclosed personal information to a client',
'Has accepted a gift, favour, or personal contact from a client',
"Doesn't see how over-involvement could harm a client",
],
maybe: [
'Understands boundaries in principle, inconsistent in application',
'Has come close to a boundary issue but caught themselves',
'Relies on the team leader to navigate boundary situations',
'Boundaries in obvious cases -- uncertain in ambiguous ones',
],
yes: [
'Has declined a gift or request from a client and handled it kindly',
'Has navigated a personal disclosure risk and managed it correctly',
'Has raised a boundary concern with their supervisor before it became a problem',
'Keeps professional and personal clearly separated in their practice',
],
strongYes: [
'Has trained or mentored others on boundary management',
'Has managed a boundary issue that others had let slide',
'Understands the harm that over-involvement causes, not just the rule',
'Boundary management is part of how they define professional care, not an external constraint',
],
},
{
criterionLabel: 'Confidentiality and information handling',
no: [
'Has shared client information with non-authorised people',
'No clear framework for what stays confidential',
'Treats confidentiality as paperwork, not a practice',
"Has disclosed something they now recognise was inappropriate",
],
maybe: [
"Knows what's confidential in obvious cases, uncertain in edge cases",
'Handles standard information correctly, hesitates on complex requests',
'Has escalated a confidentiality question but slowly',
'Privacy awareness is rule-following, not judgement',
],
yes: [
'Has declined a request for client information and described exactly how',
'Knows what a mandatory disclosure obligation is and when it applies',
'Has escalated a confidentiality concern before it became a problem',
'Handles all client information with the same level of care',
],
strongYes: [
'Has managed a mandatory disclosure situation correctly and can describe it',
'Has identified and raised a gap in how the organisation handled client information',
'Other workers ask them about confidentiality decisions',
'Confidentiality is a professional value they hold regardless of supervision',
],
},
{
criterionLabel: 'Observation and escalation',
no: [
"Has missed changes in a client's condition or behaviour",
'Escalation is for emergencies only -- everything else can wait',
'Observation notes are completed but not actively used',
"Changes in a client are for the nurse or clinical team, not their concern",
],
maybe: [
'Notices changes when obvious, misses subtle ones',
'Has escalated once appropriately, not a reliable pattern',
'Observation is done routinely, not analytically',
'Raises concerns when confident, holds back when uncertain',
],
yes: [
'Has noticed a change in a client and escalated before it became a crisis',
'Observation is something they do actively, not just document',
'Has a specific story of an early escalation that made a difference',
'Knows what to look for and why it matters clinically',
],
strongYes: [
'Has prevented a serious incident through early observation and escalation',
'Clinical team relies on their observations as part of the care assessment',
"Has improved a team's observation and recording practice",
'Early detection is part of how they define their role',
],
},
{
criterionLabel: 'Reliability and team communication',
no: [
"Client care is affected by their absence and they don't own it",
'Handover is brief or incomplete',
'Communication with the team is reactive',
"Reliability varies with how they're feeling",
],
maybe: [
'Shows up consistently in normal periods, variable when pressured',
'Handover is adequate but not thorough',
'Communicates when they have to, not proactively',
'Reliable most of the time -- occasional patterns of concern',
],
yes: [
'Consistent attendance with clear reasons for any gaps',
'Handover is thorough and documented -- they know another worker will rely on it',
'Communicates proactively when something has changed for a client',
'Others depend on them being there and they take that seriously',
],
strongYes: [
"Team leader's first call when cover is needed for a complex client",
'Handover is cited as a model by other team members',
'Has held a caseload through a difficult period without dropping anything',
"Reliability is something they treat as a professional standard for the client's sake",
],
},
],
complianceGatesMeta: [
{ label: 'Police vetting -- Vulnerable Children Act 2014', level: 'hard' },
{ label: 'NZ Certificate in Health and Wellbeing (or equivalent)', level: 'conditional' },
{ label: 'Driver licence (if transport-to-client required)', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 15: ECE Teacher ───────────────────────────────────────────────────
eceTeacher: {
anchors: [
{
criterionLabel: 'Child development knowledge and application',
no: [
'Talks about activities and routines, not development outcomes',
"Can't connect their practice to a development principle",
'Child development knowledge is theoretical, not applied',
'Planning is activity-led, not child-led',
],
maybe: [
'Some development knowledge, applied unevenly',
"Plans activities but hasn't connected them to individual outcomes",
"Knows Te Whāriki as a framework, doesn't use it in daily practice",
"Development support is reactive -- responds to needs, doesn't anticipate them",
],
yes: [
"Describes their planning in terms of individual children's developmental goals",
'Connects specific activities to specific learning outcomes',
"Has modified an approach based on a child's developmental stage",
'Te Whāriki is a working tool, not a compliance document in their practice',
],
strongYes: [
'Has designed a learning environment change based on developmental observation',
"Has supported a child with complex developmental needs within the centre's framework",
'Other teachers ask their advice on supporting individual children',
'Child development is the lens through which they plan and reflect',
],
},
{
criterionLabel: 'Safeguarding and child protection',
no: [
"Child protection is the centre manager's job",
"Can't describe their mandatory reporting obligations",
'Has missed or deprioritised a safeguarding concern',
'Sees safeguarding as a compliance exercise, not a child safety practice',
],
maybe: [
"Knows they have obligations but uncertain on the specifics",
"Has escalated a concern once but not through the right channel",
"Awareness of signs of abuse -- less certain about what to do",
"Safeguarding is for serious incidents -- hasn't thought about the continuum",
],
yes: [
'Can describe NZ mandatory reporting obligations without prompting',
'Has escalated a safeguarding concern and can describe the process',
"Knows the signs they're watching for and can name them",
'Understands the difference between mandatory reporting and professional judgement',
],
strongYes: [
'Has managed a mandatory notification and navigated it correctly',
'Has trained or guided colleagues through a safeguarding situation',
'Sees safeguarding as the foundation of the role, not an add-on',
'Has identified a systemic safeguarding gap and raised it with management',
],
},
{
criterionLabel: 'Whānau relationships and communication',
no: [
'Communication with whānau is transactional and one-directional',
"Can't describe managing a difficult whānau conversation",
"Parent concerns are passed to the centre manager",
"Whānau are kept at the door, not brought into the child's learning",
],
maybe: [
"Communicates information well, less confident managing concerns",
'Has one example of a difficult whānau conversation, not a pattern',
"Whānau involvement is invited but not designed for",
'Communication is consistent in easy cases, variable in hard ones',
],
yes: [
'Has navigated a difficult conversation with whānau and resolved it',
"Whānau are active participants in the child's portfolio and learning plan",
"Communicates proactively -- doesn't wait for parents to ask",
'Has adapted their communication for different cultural and language needs',
],
strongYes: [
"Whānau trust them with concerns they wouldn't raise with others at the centre",
'Has turned a difficult whānau relationship into a strong collaborative one',
'Has improved how the centre communicates with whānau systematically',
'Sees whānau partnership as the basis of effective ECE, not an add-on',
],
},
{
criterionLabel: 'Team practice and professional reflection',
no: [
"Reflects alone, doesn't contribute to team inquiry",
"Peer feedback is uncomfortable -- prefers to manage independently",
"Has contributed to a team problem and doesn't own their part",
'Professional development is something that happens to them',
],
maybe: [
"Participates in team practice but doesn't lead in it",
'Some reflection habit, not systematic',
"Has contributed to a team discussion but not changed their practice",
'Professional development is attended, not applied',
],
yes: [
'Describes a specific change to their practice based on team inquiry',
'Has given and received peer feedback and improved',
'Contributes to team planning with specific examples and observations',
'Reflects on their practice regularly and can describe how',
],
strongYes: [
'Has led a team inquiry cycle or professional development session',
"Their reflection is cited by colleagues as raising the team's standard",
'Has changed a team practice through their professional contribution',
'Treats collegial inquiry as how the profession grows, not just a requirement',
],
},
{
criterionLabel: 'Physical environment and routine management',
no: [
"Environment is set up by management -- not their responsibility",
"Routines are followed when supervised, not owned",
'Safety checks are a task, not a mindset',
"Can't describe how the environment supports the programme",
],
maybe: [
'Maintains the environment to the required standard',
"Follows routines consistently, hasn't improved them",
'Safety is reactive -- checks when reminded',
"Some awareness of how environment supports learning, not applied to their planning",
],
yes: [
"Has changed or improved a centre environment to better support children's learning",
"Routines are owned and modelled consistently regardless of who's watching",
'Safety checks are embedded in their daily practice without prompting',
"Describes the link between environment and children's engagement specifically",
],
strongYes: [
'Has redesigned a learning space and can describe the learning impact',
"Routines they've established are still running after they've moved on",
'Has identified and addressed a safety gap others had missed',
'Environmental design is part of how they plan, not an afterthought',
],
},
],
complianceGatesMeta: [
{ label: 'Police vetting -- Vulnerable Children Act 2014', level: 'hard' },
{ label: 'ECE teaching registration (or enrolment)', level: 'hard' },
{ label: 'First Aid certificate', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 16: Delivery Driver / Courier ────────────────────────────────────
deliveryDriver: {
anchors: [
{
criterionLabel: 'Route management and efficiency',
no: [
"Follows the GPS, doesn't think beyond it",
"Can't describe adapting a route when conditions change",
'No sense of their delivery sequence or priority logic',
'Efficiency is luck or good traffic',
],
maybe: [
'Manages routes adequately in normal conditions',
'Adjusts when obviously blocked, not proactively',
"Uses navigation but hasn't optimised it",
'Gets all deliveries done, not always in the best order',
],
yes: [
'Plans their run before leaving the depot',
'Adjusts sequence proactively when conditions change',
'Has improved their own efficiency meaningfully over time',
"Knows the area well enough to override GPS when it's wrong",
],
strongYes: [
'Has the fastest or most reliable run record in a previous team',
'Has identified a route improvement that others now use',
'Treats time efficiency as a professional discipline, not just a target',
'Delivery per hour is something they track and improve',
],
},
{
criterionLabel: 'Vehicle safety and compliance',
no: [
'Vehicle checks are for the mechanic',
'Has driven with a known defect to finish the job',
'Safety is about not having accidents -- not about prevention',
'Road rules are guidelines when running late',
],
maybe: [
'Does pre-trip checks when reminded',
'Safety-aware in normal conditions, shortcuts when pressured',
"Knows the obligations but doesn't consistently meet them",
'No serious incidents -- by luck as much as discipline',
],
yes: [
'Does pre-trip checks every day without being told',
"Has refused to drive a vehicle that wasn't roadworthy",
'Knows their obligations under the Land Transport Act',
'Has reported a near-miss or incident correctly and promptly',
],
strongYes: [
'Zero incident record across a sustained driving period',
'Has raised a fleet safety concern that led to a change',
'Vehicle safety is a professional value, not a compliance box',
'Others follow their safety standard as the benchmark',
],
},
{
criterionLabel: 'Customer handling and delivery standards',
no: [
'Delivery is the job -- customer interaction is a side task',
"Can't describe managing a difficult delivery situation",
'Leaves parcels in wrong locations without noting it',
"Customer complaints are the depot's problem",
],
maybe: [
'Polite and adequate with customers',
'Has managed a difficult delivery situation once, not a pattern',
'Follows delivery protocols, limited judgement calls',
'Customer experience is okay -- not something they think about',
],
yes: [
'Has managed a customer who was difficult at the door and resolved it',
'Knows the delivery protocols and why they exist',
'Leaves clear notes and follows safe drop procedures correctly',
'Treats each delivery as a customer touchpoint, not just a task',
],
strongYes: [
'Has received positive customer feedback by name in a previous role',
'Turned a failed delivery situation into a positive customer outcome',
'Safe drop judgement is something colleagues ask them about',
'Customer experience is the job -- not just the parcel',
],
},
{
criterionLabel: 'Time management under pressure',
no: [
'Behind from the first hour and catches up by rushing',
'No planning before the run -- reactive throughout',
'Time pressure causes visible quality shortcuts',
'Pressure creates risk -- they know it and accept it',
],
maybe: [
'Manages time adequately in normal days',
'Falls behind in high-volume periods without adjusting strategy',
'Catches up by rushing later -- not sustainable',
"Time management is okay, not something they've refined",
],
yes: [
'Plans the run to account for known pressure points',
'Has managed a high-volume day without quality shortcuts',
'Adjusts strategy mid-run when falling behind -- not just speed',
"Time pressure doesn't produce safety trade-offs in their approach",
],
strongYes: [
'Has maintained quality and safety through the busiest periods',
"Others in the depot follow their preparation approach",
'Has specifically reduced their own time per delivery through technique',
'Time management and safety are never in conflict in how they think',
],
},
{
criterionLabel: 'Reliability and self-accountability',
no: [
'Missing or late deliveries are explained away',
'Does not follow up when something goes wrong',
'Reliability varies with how the day is going',
'Accountability is filing a report -- not owning the outcome',
],
maybe: [
'Mostly reliable, occasional issues with plausible reasons',
'Follows up when chased, not proactively',
'Takes responsibility in principle, inconsistent in practice',
'Reliability is good in easy weeks, less so in hard ones',
],
yes: [
'Has escalated a problem before it became a customer complaint',
'Owns a delivery error and describes what they did to fix it',
'Consistent attendance across peak and normal periods',
'Accountability is something they describe as a professional value',
],
strongYes: [
'Has held a run together during a difficult period (sick cover, weather, volume spike)',
'Has proactively improved something that was causing reliability issues',
'Zero pattern of unexplained delivery failures or attendance gaps',
'Others rely on them specifically because of their reliability record',
],
},
],
complianceGatesMeta: [
{ label: 'NZ driver licence -- appropriate class', level: 'hard' },
{ label: 'Clean driving record', level: 'hard' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 17: Electrician ───────────────────────────────────────────────────
electrician: {
anchors: [
{
criterionLabel: 'Technical electrical competence',
no: [
"Registered but can't describe specific work they've led",
'Technical knowledge is theoretical, not applied',
'Relies on supervision for anything beyond standard installs',
"Can't describe a technical problem they diagnosed and solved",
],
maybe: [
'Competent on standard installs, less confident on complex or commercial work',
'Some diagnostic experience, thin on detail',
"Has worked in multiple settings but can't describe the differences in scope",
'Technical skill is adequate, not strong',
],
yes: [
'Names specific work types (residential, commercial, industrial) with confidence',
'Has diagnosed and solved a complex electrical fault independently',
'Understands when work requires sign-off or supervision under their licence class',
'Produces compliant work consistently -- not just when inspected',
],
strongYes: [
'Has led a complex project (new build electrical fit-out, commercial rewire)',
'Has identified a code compliance issue in existing work and managed the remediation',
'Other electricians reference their diagnostic or technical approach',
'Technical excellence is something they take personal pride in beyond the requirement',
],
},
{
criterionLabel: 'Health, safety, and electrical compliance',
no: [
"Compliance is the inspector's job",
'Has taken a shortcut on isolating a circuit to save time',
'Safety rules are followed when observed',
"Lock-out/tag-out is a general principle, not a practice they describe specifically",
],
maybe: [
'Compliant in supervised environments, variable when working alone',
'Has followed safety protocols consistently in most situations',
'Awareness of isolation procedures without specific examples',
'Safety is important in theory -- practice stories are thin',
],
yes: [
'Describes isolation and lock-out/tag-out as a personal non-negotiable',
"Has refused to work on a site that wasn't safe and described what happened",
'Has reported a compliance issue (substandard existing work, unsafe installation) formally',
'Safety discipline holds when no one is watching',
],
strongYes: [
'Has stopped a job because a condition created immediate risk',
'Has identified a compliance issue in work another tradesperson did and managed the process correctly',
'Safety leadership is something others on site follow',
"Has improved a site's safety practice through their influence",
],
},
{
criterionLabel: 'Problem diagnosis and fault-finding',
no: [
'Replaces components rather than diagnosing faults',
"Can't describe a systematic approach to fault-finding",
"Calls for help when a fault isn't immediately obvious",
'Diagnosis is trial and error -- not systematic',
],
maybe: [
'Has diagnosed standard faults, uncertain on complex ones',
'Systematic in approach, slow to narrow the problem',
'Some fault-finding experience, not across varied environments',
'Confident on domestic faults, less so on commercial or industrial',
],
yes: [
'Describes a specific fault-finding methodology they use',
'Has diagnosed a complex or intermittent fault and can walk through their reasoning',
'Uses test equipment competently (multimeter, insulation tester, thermal imaging)',
'Narrows the fault before opening or replacing anything',
],
strongYes: [
'Has diagnosed a fault that others had missed or misdiagnosed',
'Fault-finding efficiency is something others on site comment on',
"Has improved a client's system reliability through diagnostic-led remediation",
'Diagnosis is the skill they value most and actively sharpen',
],
},
{
criterionLabel: 'Client and site communication',
no: [
"Client communication is the project manager's job",
"Can't describe explaining an electrical issue to a non-technical client",
'Leaves the site without briefing the client or owner',
'Communication with other trades is minimal',
],
maybe: [
'Communicates adequately when required',
'Has explained something to a client but the example is vague',
'Communicates with the team but not proactively',
"Client-facing communication is a weakness they acknowledge",
],
yes: [
'Has explained a fault or upgrade to a client in plain language',
'Coordinates with other trades around their work sequence',
'Leaves sites with the client informed about what was done',
'Has managed a client concern about cost or timeline directly',
],
strongYes: [
'Clients specifically request them by name for repeat work',
'Has managed a complex client expectation (insurance claim, compliance issue) without the project manager',
'Communication is how they build a reputation for repeat business',
'Other tradies ask them to manage client conversations on shared sites',
],
},
{
criterionLabel: 'Self-management and site organisation',
no: [
'Tools are where they left them -- which is not always the right place',
'Materials planning is reactive',
'Runs out of stock mid-job',
"Time on site is variable and doesn't estimate accurately",
],
maybe: [
'Organised on familiar jobs, struggles on unfamiliar ones',
'Materials prep is mostly right, occasional gaps',
'Estimates are okay, miss on complex jobs',
'Self-management is adequate -- not a strength',
],
yes: [
'Plans materials and tools before arriving on site',
"Time estimates are accurate and they flag it early when they won't hit them",
'Site is clean and organised throughout the job, not just at the end',
'Has never run out of materials mid-job due to poor planning',
],
strongYes: [
'Job management (materials, time, site) is something other tradies comment on',
'Has taken over a disorganised job and brought it into shape',
'Estimates are tight and have been accurate across diverse job types',
'Organisation is something they treat as part of the craft',
],
},
],
complianceGatesMeta: [
{ label: 'NZ Electrical Worker registration -- EWRB', level: 'hard' },
{ label: 'Electrical Certificate of Compliance (CoC) issuing authority', level: 'hard' },
{ label: 'Site Safe certification', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

// ─── Role 18: Plumber ───────────────────────────────────────────────────────
plumber: {
anchors: [
{
criterionLabel: 'Technical plumbing competence',
no: [
"Registered but can't describe specific work they've led",
"Can't name a technical problem they diagnosed independently",
'Relies on supervision for anything beyond standard installs',
'Technical knowledge is adequate at the minimum level',
],
maybe: [
'Competent on standard residential, uncertain on commercial or complex work',
'Has done varied work but the examples are thin',
'Diagnosis is straightforward faults only',
"Technical skill is adequate, not something they've developed past certification",
],
yes: [
'Names specific job types (hot water, drainage, gas fitting, commercial) with confidence',
'Has diagnosed and solved a complex plumbing fault independently',
'Understands compliance requirements for different work types',
'Produces compliant work consistently -- inspections are not their concern',
],
strongYes: [
'Has led a complex project (multi-unit, commercial fit-out, drainage system)',
'Has identified a code compliance issue in existing work and managed the remediation',
'Others reference their diagnostic or technical approach',
'Technical excellence is a personal standard beyond the regulatory requirement',
],
},
{
criterionLabel: 'Health, safety, and plumbing compliance',
no: [
"Compliance is the inspector's job",
"Has signed off work they weren't confident about to finish the job",
'Safety rules are followed when watched',
'No specific story of catching a compliance issue',
],
maybe: [
'Compliant in supervised environments, variable working alone',
"Has followed protocols consistently in most situations but can't name a specific example",
"Aware of requirements -- practice stories are thin",
"Safety is important in principle -- variable under time pressure",
],
yes: [
"Has refused to proceed on a job because something wasn't right",
'Has identified non-compliant existing work and managed the process correctly',
'Compliance documentation (certificates, as-builts) is something they complete, not delegate',
'Safety discipline holds when no one is watching',
],
strongYes: [
'Has stopped a job that would have created a public health risk',
"Has improved a site's compliance practice through their leadership",
'Compliance quality is something clients notice and mention',
'Others follow their compliance standards as the site benchmark',
],
},
{
criterionLabel: 'Fault diagnosis and problem-solving',
no: [
'Replaces components before diagnosing the root cause',
"Can't describe their fault-finding approach",
"Calls for help when the fault isn't obvious",
'Diagnosis is guesswork -- not systematic',
],
maybe: [
'Diagnoses standard faults, uncertain on complex or intermittent ones',
'Methodical but slow -- takes longer than it should to narrow the problem',
"Has diagnosed varied faults but can't describe the technique",
'Confident on domestic, less so on commercial or drainage',
],
yes: [
'Describes a specific approach to diagnosing a plumbing fault',
'Has traced an intermittent or hidden fault and can walk through their reasoning',
'Uses test equipment and inspection correctly before opening up or replacing',
'Gets to the root cause before starting work',
],
strongYes: [
'Has diagnosed a fault that others misidentified',
'Fault-finding speed and accuracy is something clients or employers comment on',
'Has prevented a recurrence through root-cause diagnosis, not just symptom repair',
'Diagnosis is the skill they value most and actively sharpen',
],
},
{
criterionLabel: 'Client and site communication',
no: [
"Client communication is the project manager's job",
'Leaves a job without explaining what was done',
"Can't describe managing a client who was unhappy with a price or timeline",
'Communication with other trades is minimal',
],
maybe: [
'Communicates adequately when required',
'Has explained a fault to a client but the example is vague',
"Client-facing communication happens, not a strength",
"Coordinates with other trades when their schedule overlaps, not proactively",
],
yes: [
'Has explained a fault or remediation to a client in plain language and they understood',
'Manages client expectations on cost and timeline proactively',
'Leaves jobs with the client fully briefed on what was done and why',
'Has resolved a client concern directly without the project manager',
],
strongYes: [
'Clients request them by name for repeat or referred work',
'Has managed a complex client situation (insurance, body corporate, landlord) without escalation',
'Communication is how they build a repeat client base',
'Other tradies ask them to handle client conversations on shared sites',
],
},
{
criterionLabel: 'Self-management and job organisation',
no: [
'Materials are ordered reactively -- runs out on site',
'Time estimates are consistently off',
"Site is organised to their convenience, not the job's requirements",
'Self-management is a concept, not a practice',
],
maybe: [
'Organised on familiar job types, struggles on complex ones',
'Materials prep is mostly right, gaps on unfamiliar jobs',
'Estimates are okay -- miss on unusual work',
'Organisation is adequate -- not deliberate',
],
yes: [
'Plans materials and tools before arriving on site',
'Estimates are accurate and they flag early when something will take longer',
'Site is clean and accessible throughout the job',
'Has never held up a project because of poor planning',
],
strongYes: [
'Job management is something other tradies comment on positively',
'Has taken over a disorganised job and brought it into order',
'Estimates are tight and accurate across diverse job types',
'Organisation is part of how they describe their professionalism',
],
},
],
complianceGatesMeta: [
{ label: 'NZ Plumbing registration -- PGDB', level: 'hard' },
{ label: 'Gasfitting licence (if gas work is in scope)', level: 'conditional' },
{ label: 'Site Safe certification', level: 'conditional' },
{ label: 'Right to work in NZ', level: 'hard' },
],
},

};

/**

- Helper: get anchor data for a role key.
- Returns null gracefully if the role key isn't in the library.
- 
- Usage:
- import { getAnchorsForRole } from './criteriaAnchorLibrary';
- const anchors = getAnchorsForRole('cafeManager');
  */
  export function getAnchorsForRole(roleKey) {
  return criteriaAnchorLibrary[roleKey] ?? null;
  }

/**

- Role key map -- maps criteriaLibrary.js role identifiers to
- criteriaAnchorLibrary keys. Update if criteriaLibrary.js uses
- different keys.
- 
- Pattern: criteriaLibrary key → criteriaAnchorLibrary key
- (If they already match, this map is redundant but harmless.)
  */
  export const ROLE_KEY_MAP = {
  builder:              'builder',
  barista:              'barista',
  kitchenHand:          'kitchenHand',
  chef:                 'chef',
  cafeManager:          'cafeManager',
  retailAssistant:      'retailAssistant',
  retailManager:        'retailManager',
  officeAdministrator:  'officeAdministrator',
  warehouseStoreperson: 'warehouseStoreperson',
  medicalReceptionist:  'medicalReceptionist',
  salesRepresentative:  'salesRepresentative',
  customerServiceRep:   'customerServiceRep',
  hairdresser:          'hairdresser',
  supportWorker:        'supportWorker',
  eceTeacher:           'eceTeacher',
  deliveryDriver:       'deliveryDriver',
  electrician:          'electrician',
  plumber:              'plumber',
  };
